import { Route, Routes } from 'react-router-dom'
import { useEffect, useState } from 'react'
import { MainLayout } from '@/shared/components/layout'
import { useAuth } from '@/shared/contexts/AuthContext'
import { useUnit } from '@/modules/units/contexts/UnitContext'
import { useResolveContextUnit } from '@/modules/units/useResolveContextUnit'
import { getUserUnitsByContext } from '@/modules/units'
import { getProfile } from '@/modules/profile'
import { AccountingDashboard } from '@/modules/accounting/Dashboard'
import { AccountingClientPortalDashboard } from '@/modules/accounting/ClientPortalDashboard'
import { AccountingClientDocumentsPage } from '@/modules/accounting/ClientDocumentsPage'
import { FileReaderPage } from '@/modules/file-reader/FileReaderPage'
import { FileLayoutsPage } from '@/modules/file-reader/FileLayoutsPage'
import { AccountingProductsPage, FiscalMemoryPage } from '@/modules/accounting/memory'

export default function AccountingLayout() {
  const { user } = useAuth()
  const {
    currentUnit,
    isLoading: unitLoading,
    setCurrentUnit: useUnitSwitch,
  } = useUnit()
  const { ready, resolving } = useResolveContextUnit('accounting')
  const [profile, setProfile] = useState<{
    full_name?: string | null
    current_unit_id?: string | null
  } | null>(null)
  const [resolvingOffice, setResolvingOffice] = useState(false)

  useEffect(() => {
    if (!user?.id) return

    getProfile(user.id)
      .then(setProfile)
      .catch((error) => console.error('[AccountingLayout] Error loading profile:', error))
  }, [user?.id])

  useEffect(() => {
    if (!ready || !user?.id || currentUnit?.attrs?.accounting_unit_type !== 'client') return

    let cancelled = false
    setResolvingOffice(true)

    getUserUnitsByContext(user.id, 'accounting')
      .then(async (units) => {
        if (cancelled) return
        const office = units.find((unit) => unit.attrs?.accounting_unit_type !== 'client')
        if (office) await useUnitSwitch(office.id)
      })
      .catch((error) => console.error('[AccountingLayout] Error restoring office unit:', error))
      .finally(() => setResolvingOffice(false))

    return () => { cancelled = true }
  }, [currentUnit?.attrs?.accounting_unit_type, ready, useUnitSwitch, user?.id])

  if (unitLoading || resolving || resolvingOffice || !ready) {
    return (
      <div className="flex h-screen items-center justify-center bg-gray-50 dark:bg-gray-950">
        <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-emerald-600 border-r-transparent" />
      </div>
    )
  }

  const isClientWorkspace = currentUnit?.attrs?.accounting_unit_type === 'client'

  return (
    <MainLayout
      userName={profile?.full_name || user?.email}
      userId={user?.id}
      currentUnitId={profile?.current_unit_id || null}
    >
      <Routes>
        <Route
          index
          element={isClientWorkspace ? <AccountingClientPortalDashboard /> : <AccountingDashboard />}
        />
        {!isClientWorkspace && (
          <>
            <Route path="clients/:partyId" element={<AccountingClientDocumentsPage />} />
            <Route path="clients/:partyId/products" element={<AccountingProductsPage />} />
            <Route path="clients/:partyId/fiscal-memory" element={<FiscalMemoryPage />} />
            <Route path="clients/:partyId/documents/:documentId/reader" element={<FileReaderPage />} />
            <Route path="layouts" element={<FileLayoutsPage />} />
          </>
        )}
      </Routes>
    </MainLayout>
  )
}
